import {observer_mgl} from "mobx-graphlink";
import {EngineConfigPanels_SharedProps} from "../../ConfigPanel_Local.js";
import {FramePool, ImageFrameFlawType} from "../../../../../Server/DB/engineConfigs/@EngineConfig/@EC_VR.js";
import React, {useState} from "react";
import {SharedProps_VRConfigUI} from "../VR_ConfigUI.js";
import {Text, Column, RowLR, TextInput, Select, Spinner, Row, Button, CheckBox} from "react-vcomponents";
import {ES, InfoButton, TextPlus} from "web-vcore";
import {Clone, GetEntries} from "js-vextensions";
import {SpinnerForPercent} from "../../../../../Utils/ReactComponents/SpinnerForPercent.js";
import {ShowMessageBox} from "react-vmessagebox";

export const FramePoolsUI = observer_mgl((props: SharedProps_VRConfigUI)=>{
	const {enabled, config, ChangeConfig} = props;
	const pools = config.frames_pools;
	const [selectedPoolIndex, setSelectedPoolIndex] = useState(0);

	return (
		<>
			<Row mt={5}>
				<Text>Pools:</Text>
				{pools.map((pool, index)=>{
					return <Button key={index} ml={5} p="2px 5px" text={`${index + 1}: ${pool.name}${!pool.enabled ? " [d]" : ""}`}
						style={ES(
							selectedPoolIndex == index && {backgroundColor: "rgba(100,200,100,.5)"},
						)}
						onClick={()=>setSelectedPoolIndex(index)}/>;
				})}
				<Button ml={5} mdIcon="plus" enabled={enabled} onClick={()=>{
					ChangeConfig(c=>c.frames_pools.push(new FramePool()));
				}}/>
			</Row>
			{pools.length > selectedPoolIndex &&
			<FramePoolUI {...props} pool={pools[selectedPoolIndex]} poolIndex={selectedPoolIndex}/>}
		</>
	);
});

const splitAt = 240;
export const FramePoolUI = observer_mgl((props: {pool: FramePool, poolIndex: number} & SharedProps_VRConfigUI)=>{
	const {pool, poolIndex, enabled, config, ChangeConfig} = props;

	const ChangePool = (poolChangerFunc: (pool: FramePool)=>void)=>{
		ChangeConfig(c=>{
			const newPool = Clone(pool) as FramePool;
			poolChangerFunc(newPool);
			c.frames_pools[props.poolIndex] = newPool;
		});
	}

	return (
		<Column ml={10}>
			<Row mt={5}>
				<Text>Name:</Text>
				<TextInput ml={5} enabled={enabled} value={pool.name} onChange={val=>ChangePool(a=>a.name = val)}/>
				<CheckBox ml={5} text="Enabled" enabled={enabled} value={pool.enabled} onChange={val=>ChangePool(a=>a.enabled = val)}/>
				<Button ml={5} mdIcon="delete" enabled={enabled} onClick={()=>{
					ShowMessageBox({
						title: "Delete pool?", cancelButton: true,
						message: `Permanently delete this pool?`,
						onOK: ()=>{
							ChangeConfig(c=>c.frames_pools.RemoveAt(poolIndex));
						},
					});
				}}/>
			</Row>
			<RowLR mt={5} splitAt={splitAt}>
				<TextPlus info={`
					The pool's weight determines its likelihood of being selected, among the set of enabled pools.
					* Note: Weight is used for "fresh pool selections"; but pools can get "locked in" as active for >1 frame. (see "Lock in..." setting below)
				`.AsMultiline(0)}>Weight:</TextPlus>
				<Spinner enabled={enabled} min={0} step={1} value={pool.weight} onChange={val=>ChangePool(a=>a.weight = val)}/>
			</RowLR>
			<RowLR mt={5} splitAt={splitAt}>
				<TextPlus info="When this pool is selected, ensure it gets between X and Y frames selected for it in a row. (no lock-in = 1)">Lock in:</TextPlus>
				<Text>Min:</Text>
				<Spinner ml={5} enabled={enabled} min={1} step={1} value={pool.lockIn_min} onChange={val=>ChangePool(a=>a.lockIn_min = val)}/>
				<Text ml={5}>Max:</Text>
				<Spinner ml={5} enabled={enabled} min={1} step={1} value={pool.lockIn_max} onChange={val=>ChangePool(a=>a.lockIn_max = val)}/>
				<CheckBox ml={5} text="Force switch after" enabled={enabled} value={pool.lockIn_forceSwitchAfter} onChange={val=>ChangePool(a=>a.lockIn_forceSwitchAfter = val)}/>
			</RowLR>
			<RowLR mt={5} splitAt={splitAt}>
				<TextPlus info="Include only image/audio files whose paths contain the specified string. (case-insensitive; ignored if empty)">Media path filter:</TextPlus>
				<TextInput enabled={enabled} value={pool.mediaPathFilter} onChange={val=>ChangePool(a=>a.mediaPathFilter = val)}/>
			</RowLR>
			<RowLR mt={5} splitAt={splitAt}>
				<TextPlus info="Exclude image/audio files whose paths contain the specified string. (case-insensitive; ignored if empty)">Media path exclude:</TextPlus>
				<TextInput enabled={enabled} value={pool.mediaPathExclude} onChange={val=>ChangePool(a=>a.mediaPathExclude = val)}/>
			</RowLR>

			<Row mt={5}>
				<CheckBox text="Play audio-clip:" value={pool.audioClip_enabled} onChange={val=>ChangePool(a=>a.audioClip_enabled = val)}/>
				<InfoButton ml={5} text={`
					If enabled, when the frame is displayed, an audio-clip will be played, from the LF audio-files folder.
					The default behavior is to play the audio-clip starting from frame-displayed moment in the episode.
					But if flaw-type is "Wrong audio clip played" AND this frame is a "lie", then the clip will be from some other season/episode of the series.
				`.AsMultiline(0)}/>
				{!pool.audioClip_enabled && pool.flaw_type == ImageFrameFlawType.wrongAudioClipPlayed &&
				<Text ml={5} style={{color: "red", fontSize: 12}}>{`With flaw-type "Wrong audio clip played", this should be enabled!`}</Text>}
			</Row>
			{pool.audioClip_enabled &&
			<Column mt={5} ml={10}>
				<RowLR mt={5} splitAt={splitAt - 10}>
					<Text>Duration:</Text>
					<Spinner enabled={enabled} min={0} step={.5} value={pool.audioClip_duration} onChange={val=>ChangePool(a=>a.audioClip_duration = val)}/>
					<Text>s</Text>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt - 10}>
					<Text mr={5}>Volume multiplier:</Text>
					<SpinnerForPercent min={0} max={999} enabled={enabled} value={pool.audioClip_volumeMultiplier} onChange={val=>ChangePool(a=>a.audioClip_volumeMultiplier = val)}/>
				</RowLR>
			</Column>}

			<Row mt={5}>
				<CheckBox text="Play video-clip:" value={pool.videoClip_enabled} onChange={val=>ChangePool(a=>a.videoClip_enabled = val)}/>
				<InfoButton ml={5} text={`
					If enabled, when the frame would be displayed, a video-clip starting from that point will be played instead, from the LF video-files folder.
					(Later, will probably have video-clips be a "dedicated source"; but for now, it is an add-on to the image-frames pool.)
				`.AsMultiline(0)}/>
			</Row>
			{pool.videoClip_enabled &&
			<Column mt={5} ml={10}>
				<RowLR mt={5} splitAt={splitAt - 10}>
					<Text>Duration:</Text>
					<Spinner enabled={enabled} min={0} step={.5} value={pool.videoClip_duration} onChange={val=>ChangePool(a=>a.videoClip_duration = val)}/>
					<Text>s</Text>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt - 10}>
					<Text mr={5}>Volume multiplier:</Text>
					<SpinnerForPercent min={0} max={999} enabled={enabled} value={pool.videoClip_volumeMultiplier} onChange={val=>ChangePool(a=>a.videoClip_volumeMultiplier = val)}/>
				</RowLR>
				<RowLR mt={5} splitAt={splitAt - 10}>
					<TextPlus mr={5} info={`
						Examples:
						* "" (no language-field value set)
						* "und" (undetermined language [often English?])
						* "jpn" (Japanese)
						* "eng" (English)
					`.AsMultiline(0)}>Audio-track (preferred lang.):</TextPlus>
					<TextInput enabled={enabled} value={pool.videoClip_audioTrack_preferredLanguage} onChange={val=>ChangePool(a=>a.videoClip_audioTrack_preferredLanguage = val)}/>
				</RowLR>
			</Column>}

			<Row mt={5}>
				<CheckBox text="Play dream-clip:" value={pool.dreamClip_enabled} onChange={val=>ChangePool(a=>a.dreamClip_enabled = val)}/>
				<InfoButton ml={5} text={`
					If enabled, when the frame is displayed, a sequence of events from a random dream will be played. (uses config of concept-link comp)
				`.AsMultiline(0)}/>
			</Row>
			<RowLR mt={5} splitAt={splitAt}>
				<TextPlus info={`
					Default basis for frame truth/lie: Based on the "flaw percentage" setting.

					Extra effects from some flaw-types:
					* If "wrongNameVoiced", then:
					1) Modifies frame-selection: First finds a "target character" (using weightings above), then an image with that character is selected.
					2) Adds name-voicing effect: If true frame, the named character is present (visible) in the frame; if lie, they're not present.
					* If "wrongAudioClipPlayed", then modifies "Play audio-clip" effect: For lie frames, audio is played from a *different* episode of the series.
					* If flaw-type is "poolChanged", then modifies truth/lie basis: Based on whether the current frame's pool differs from previous frame's.
				`.AsMultiline(0)}>Flaw type:</TextPlus>
				<Select enabled={enabled} options={GetEntries(ImageFrameFlawType, "ui")} value={pool.flaw_type} onChange={val=>ChangePool(a=>a.flaw_type = val)}/>
			</RowLR>
			{pool.flaw_type != ImageFrameFlawType.poolChanged &&
			<RowLR mt={5} splitAt={splitAt}>
				<Text>Flaw percentage:</Text>
				<SpinnerForPercent enabled={enabled} value={pool.flaw_percent} onChange={val=>ChangePool(a=>a.flaw_percent = val)}/>
			</RowLR>}
			{pool.flaw_type == ImageFrameFlawType.wrongNameVoiced && <>
				<Row mt={5}>
					<Text>Characters:</Text>
					<Button ml={5} mdIcon="plus" enabled={enabled} onClick={()=>{
						ChangePool(a=>{
							if (!a.characters) a.characters = [];
							a.characters.push({initials: "NC", name_voicing: "New Character", weight: 1});
						});
					}}/>
				</Row>
				{(pool.characters ?? []).map((character, index)=>{
					return (
						<Row key={index} mt={5}>
							<Text style={{minWidth: 20}}>{index + 1}:</Text>
							<Text>Initials:</Text>
							<TextInput ml={5} enabled={enabled} style={{width: 50}} value={character.initials} onChange={val=>ChangePool(a=>a.characters![index].initials = val)}/>
							<Text ml={5}>Name (voicing):</Text>
							<TextInput ml={5} enabled={enabled} style={{width: 150}} value={character.name_voicing} onChange={val=>ChangePool(a=>a.characters![index].name_voicing = val)}/>
							<Text ml={5}>Weight:</Text>
							<Spinner ml={5} enabled={enabled} min={0} step={.05} value={character.weight} onChange={val=>ChangePool(a=>a.characters![index].weight = val)}/>
							<Button ml={5} mdIcon="arrow-up-bold" enabled={enabled && index > 0} onClick={()=>{
								ChangePool(a=>{
									const charInPool = a.characters![index];
									a.characters!.Move(charInPool, index - 1, "final-index");
								});
							}}/>
							<Button ml={5} mdIcon="arrow-down-bold" enabled={enabled && index < pool.characters.length - 1} onClick={()=>{
								ChangePool(a=>{
									const charInPool = a.characters![index];
									a.characters!.Move(charInPool, index + 1, "final-index");
								});
							}}/>
							<Button ml={5} mdIcon="delete" enabled={enabled} onClick={()=>{
								ShowMessageBox({
									title: `Delete "${character.name_voicing}"?`, cancelButton: true,
									message: `Delete character named "${character.name_voicing}", with initials "${character.initials}"?`,
									onOK: ()=>{
										ChangePool(a=>a.characters!.RemoveAt(index));
									},
								});
							}}/>
						</Row>
					);
				})}
			</>}
		</Column>
	);
});